// Admin landing page: what needs attention, then the numbers.
//
// Everything here is derived from the existing list endpoints rather than a
// dedicated stats API — at this volume a couple of list calls is cheaper
// than building and maintaining an aggregate endpoint.
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, Package, Plus, ShoppingBag, Sparkles } from 'lucide-react';
import {
  adminGetInquiries,
  adminGetOrders,
  describeApiError,
  getProducts,
} from '../../services/api';
import type { Inquiry, Order, OrderStatus, Product } from '../../types';
import { formatCents } from '../../utils/money';
import { STATUS_LABEL } from './orderUtils';
import { statusLabel } from '../../utils/orderProgress';

// Statuses that mean money is still owed or work is outstanding.
const OPEN_STATUSES: OrderStatus[] = [
  'created',
  'invoice_sent',
  'paid',
  'in_progress',
  'ready_for_pickup',
];

const Stat: React.FC<{ label: string; value: React.ReactNode; hint?: string }> = ({
  label,
  value,
  hint,
}) => (
  <div className="bg-white rounded border border-background-dark p-4">
    <div className="text-xs uppercase tracking-wide text-text-light">{label}</div>
    <div className="text-2xl mt-1 font-sans">{value}</div>
    {hint && <div className="text-xs text-text-light mt-0.5">{hint}</div>}
  </div>
);

const AdminDashboard: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    Promise.all([
      adminGetOrders(),
      adminGetInquiries(),
      // include_private + active_only=false so drafts count too.
      getProducts(false, true),
    ])
      .then(([o, i, p]) => {
        setOrders(o);
        setInquiries(i);
        setProducts(p);
      })
      .catch(e => setError(describeApiError(e, 'Could not load dashboard')))
      .finally(() => setLoading(false));
  }, []);

  const stats = useMemo(() => {
    const open = orders.filter(o => OPEN_STATUSES.includes(o.status));
    const awaitingPayment = orders.filter(o => o.status === 'invoice_sent');
    // Storefront submissions sitting at `created` haven't been priced or
    // invoiced yet — these are the ones that need action first.
    const needsReview = orders.filter(o => o.status === 'created');
    const newInquiries = inquiries.filter(i => i.status === 'new');
    const outstandingCents = awaitingPayment.reduce((sum, o) => sum + o.total_cents, 0);
    const paidCents = orders
      .filter(o => o.paid_at)
      .reduce((sum, o) => sum + o.total_cents, 0);

    return {
      open,
      awaitingPayment,
      needsReview,
      newInquiries,
      outstandingCents,
      paidCents,
      activeProducts: products.filter(p => p.status === 'active').length,
      draftProducts: products.filter(p => p.status !== 'active').length,
    };
  }, [orders, inquiries, products]);

  const recentOrders = useMemo(
    () => [...orders].sort((a, b) => b.created_at.localeCompare(a.created_at)).slice(0, 5),
    [orders]
  );

  if (loading) return <div className="text-text-light">Loading…</div>;

  return (
    <div className="space-y-8">
      <h1 className="text-3xl">Dashboard</h1>

      {error && <div className="text-sm text-red-600">{error}</div>}

      {/* Things waiting on the shop. Hidden entirely when nothing is due, so
          an empty state doesn't read as a to-do list. */}
      {(stats.needsReview.length > 0 || stats.newInquiries.length > 0) && (
        <section className="space-y-2">
          {stats.needsReview.length > 0 && (
            <Link
              to="/admin/orders"
              className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded px-4 py-3 text-sm hover:border-amber-400"
            >
              <AlertCircle size={18} className="text-amber-700 shrink-0" />
              <span>
                <strong>{stats.needsReview.length}</strong>{' '}
                {stats.needsReview.length === 1 ? 'request needs' : 'requests need'} pricing
                and an invoice
              </span>
            </Link>
          )}
          {stats.newInquiries.length > 0 && (
            <Link
              to="/admin/inquiries"
              className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded px-4 py-3 text-sm hover:border-amber-400"
            >
              <Sparkles size={18} className="text-amber-700 shrink-0" />
              <span>
                <strong>{stats.newInquiries.length}</strong> new custom{' '}
                {stats.newInquiries.length === 1 ? 'inquiry' : 'inquiries'} to review
              </span>
            </Link>
          )}
        </section>
      )}

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Open orders" value={stats.open.length} hint="Not yet completed" />
        <Stat
          label="Awaiting payment"
          value={formatCents(stats.outstandingCents)}
          hint={`${stats.awaitingPayment.length} invoice${stats.awaitingPayment.length === 1 ? '' : 's'} sent`}
        />
        <Stat label="Collected" value={formatCents(stats.paidCents)} hint="All paid orders" />
        <Stat
          label="Products"
          value={stats.activeProducts}
          hint={stats.draftProducts > 0 ? `${stats.draftProducts} draft/archived` : 'All published'}
        />
      </section>

      <section>
        <h2 className="text-2xl mb-3">Quick actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Link
            to="/admin/orders/new"
            className="flex items-center gap-2 bg-primary text-white rounded px-4 py-3 text-sm font-medium hover:bg-primary-dark"
          >
            <Plus size={16} /> New order
          </Link>
          <Link
            to="/admin/products/new"
            className="flex items-center gap-2 border border-background-dark bg-white rounded px-4 py-3 text-sm hover:border-primary"
          >
            <Package size={16} /> Add a product
          </Link>
          <Link
            to="/admin/featured"
            className="flex items-center gap-2 border border-background-dark bg-white rounded px-4 py-3 text-sm hover:border-primary"
          >
            <ShoppingBag size={16} /> Add featured photos
          </Link>
        </div>
      </section>

      <section>
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="text-2xl">Recent orders</h2>
          <Link to="/admin/orders" className="text-sm text-primary hover:underline">
            View all →
          </Link>
        </div>
        {recentOrders.length === 0 ? (
          <div className="text-sm text-text-light border border-dashed border-background-dark rounded p-6 text-center">
            No orders yet.
          </div>
        ) : (
          <div className="bg-white border border-background-dark rounded divide-y divide-background-dark">
            {recentOrders.map(o => (
              <Link
                key={o.order_id}
                to={`/admin/orders/${o.order_id}`}
                className="flex items-center justify-between gap-3 px-4 py-3 text-sm hover:bg-background-dark/30"
              >
                <div className="min-w-0">
                  <div className="font-medium truncate">{o.customer_name}</div>
                  <div className="text-xs text-text-light font-mono">{o.order_number}</div>
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  <span className="text-xs text-text-light">
                    {statusLabel(STATUS_LABEL, o.status, o.fulfillment)}
                  </span>
                  <span>{formatCents(o.total_cents)}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminDashboard;
